import { Loader2 } from 'lucide-react'

import { cn } from '@/utils/cn'

import type { LoadingStateProps } from '../DataTable.types'

export const TableLoading: React.FC<LoadingStateProps> = ({ message = 'Cargando datos...' }) => {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
      <p className="text-muted-foreground text-center">{message}</p>
    </div>
  )
}

// Table row loading skeleton
export const TableRowSkeleton: React.FC<{
  columns: number
  rows?: number
  className?: string
}> = ({ columns, rows = 5, className }) => (
  <tbody className={className}>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <tr key={rowIndex} className="border-b">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <td key={colIndex} className="p-4">
            <div
              className={cn(
                'h-4 bg-muted rounded animate-pulse',
                colIndex === 0 && 'w-24',
                colIndex === 1 && 'w-32',
                colIndex === 2 && 'w-20',
                colIndex >= 3 && 'w-16'
              )}
            />
          </td>
        ))}
      </tr>
    ))}
  </tbody>
)

// Full table loading state with skeleton
export const TableLoadingSkeleton: React.FC<{
  columns: number
  rows?: number
  showHeader?: boolean
  className?: string
}> = ({ columns, rows = 5, showHeader = true, className }) => (
  <div className={cn('w-full overflow-auto', className)}>
    <table className="w-full">
      {showHeader && (
        <thead>
          <tr className="border-b">
            {Array.from({ length: columns }).map((_, index) => (
              <th key={index} className="p-4 text-left">
                <div className="h-4 w-20 bg-muted rounded animate-pulse" />
              </th>
            ))}
          </tr>
        </thead>
      )}
      <TableRowSkeleton columns={columns} rows={rows} />
    </table>
  </div>
)

// Inline loading for specific cells
export const CellLoading: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn('h-4 w-16 bg-muted rounded animate-pulse', className)} />
)

// Loading overlay for tables
export const LoadingOverlay: React.FC<{
  isVisible: boolean
  message?: string
  className?: string
}> = ({ isVisible, message = 'Cargando...', className }) => {
  if (!isVisible) return null

  return (
    <div
      className={cn(
        'absolute inset-0 bg-background/80 backdrop-blur-sm',
        'flex items-center justify-center z-50',
        className
      )}
    >
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-6 w-6 animate-spin" />
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  )
}
